import { isEqual } from 'lodash-es'
import { useParams, usePathname } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import {
  CartLine,
  GetCartVariantsQuery,
  GetZidiCartVariantsQuery,
  ProductCatalogVariantGroup,
} from '../../../__generated__/graphql/catalog/graphql'
import { createPdpUrl } from '../../../app/_components/navigation/createPdpUrl'
import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { CartStoreLineItem } from '../../../lib/adapters/cart-persistence/useCartStore'
import { ZidiGtmVariant } from '../../../lib/hooks/useGoogleTagManager/types'
import { getDollarsAndCentsForAmount } from '../../../lib/utils/currency'
import { CartItem, HydratedCartItemFields } from '../../../types/figs'

import { UseCartNewCartItem } from '.'

type ExternalCartItem = Omit<CartStoreLineItem, 'cost' | 'discounts'>

type VariantTypes =
  | NonNullable<GetZidiCartVariantsQuery['variants']>['nodes'][number]
  | GetCartVariantsQuery['productCatalogVariants'][number]

type VariantsBySku = Record<string, VariantTypes>

export function augmentPersistedCartItemsWithFigsData(
  persistedCartItems: ExternalCartItem[],
  variantsBySku: VariantsBySku,
  locale: string
): CartItem[] {
  return persistedCartItems.map((item): CartItem => {
    return augmentCartStoreLineItem(item, variantsBySku, locale)
  })
}

export const blankCartItem = {
  isLowInventory: false,
  image: {
    alt: '',
    url: undefined,
  },
  fullPrice: 0,
  effectivePrice: 0,
  finalSale: false,
  productGroupHandle: '',
  productType: '',
  title: '',
  variantId: '',
  colorId: undefined,
  colorDisplayValue: undefined,
  fitId: undefined,
  fitDisplayValue: undefined,
  sizeId: undefined,
  sizeDisplayValue: undefined,
  shopifyProductId: '',
  shopifyVariantId: '',
  currencyCode: 'USD',
  gender: '',
  swPromoEligible: true,
  categoryId: '',
  availableForSale: true,
  urlPath: '#',
} satisfies Partial<CartItem>

function getBasicFields(item: ExternalCartItem): CartItem {
  return {
    ...blankCartItem,
    key: item.cartLineId,
    cartStoreKey: item.cartLineId,
    sku: item.sku,
    quantity: item.quantity,
    properties: item.attributes.reduce((accumulator, current) => {
      return Object.assign(accumulator, { [current.key]: current.value })
    }, {}),
  }
}

function getHydratedFields(
  variantData: VariantTypes,
  discountMultiplier: number,
  locale: string
): HydratedCartItemFields {
  if ('__typename' in variantData && variantData.__typename === 'ProductCatalogVariant') {
    return {
      colorId: variantData.group.color?.id,
      colorDisplayValue: variantData.group.color?.displayValue,
      image: {
        alt: variantData.group.title,
        url: variantData.images[0]?.url,
      },
      fitDisplayValue: variantData.fit?.id === 'regular' ? null : variantData.fit?.displayValue,
      fitId: variantData.fit?.id,
      fullPrice: getDollarsAndCentsForAmount({
        value: variantData.compareAtPrice?.amount ?? variantData.price.amount,
        unit: 'dollars',
        currency: variantData.price.currencyCode,
        locale,
      }).cents,
      shopifyProductId: variantData.shopifyProductId,
      effectivePrice: getDollarsAndCentsForAmount({
        value:
          !!variantData.compareAtPrice?.amount && discountMultiplier < 1
            ? variantData.compareAtPrice.amount * discountMultiplier
            : variantData.price.amount * discountMultiplier,
        unit: 'dollars',
        currency: variantData.price.currencyCode,
        locale,
      }).cents,
      productGroupHandle: variantData.group.product.handle,
      sizeId: variantData.size?.id,
      sizeDisplayValue: variantData.size?.displayValue,
      title: variantData.group.title,
      finalSale: variantData.group.finalSale,
      categoryId: variantData.group.primaryCategory.analyticsId,
      currencyCode: variantData.price.currencyCode,
      gender: variantData.group.gender,
      availableForSale: variantData.availability !== 'UNAVAILABLE',
      urlPath: variantData.urlPath,
      shopifyVariantId: variantData.shopifyId,
      swPromoEligible: variantData.group.siteWidePromoEligible,
      isLowInventory: variantData.stockState === 'LOW_STOCK',
    }
  } else {
    const price =
      variantData.priceDetails?.discountPrice?.amount ?? variantData.priceDetails!.price!.amount
    const effectivePrice = Math.round(price * discountMultiplier)

    return {
      colorId: variantData.rawColor,
      colorDisplayValue: variantData.color,
      image: {
        alt: variantData.productGroup.title,
        url: variantData.image?.source,
      },
      fitId: variantData.rawFit,
      fitDisplayValue: variantData.rawFit === 'regular' ? null : variantData.fit,
      fullPrice: variantData.priceDetails!.price!.amount,
      shopifyVariantId: variantData.shopifyId,
      shopifyProductId: variantData.externalParentId,
      effectivePrice,
      productGroupHandle: variantData.productGroup.handle,
      sizeId: variantData.rawSize,
      sizeDisplayValue: variantData.size,
      title: variantData.productGroup.title,
      finalSale: variantData.product.finalSale,
      categoryId: variantData.productGroup.rawCategory,
      currencyCode: variantData.priceDetails!.price!.currency,
      gender: variantData.productGroup.genderCategory,
      availableForSale: variantData.product.availableForSale,
      swPromoEligible: variantData.product.swPromoEligible,
      urlPath: createPdpUrl({
        productGroupHandle: variantData.productGroup.handle,
        collectionHandle: undefined,
        isKit: false,
        isExclusive: variantData.product.exclusive,
        genderKey: variantData.productGroup.genderCategory,
        colorKey: variantData.rawColor,
        fitKey: variantData.rawFit,
        sizeKey: variantData.rawSize,
      }),
      isLowInventory: variantData.isLowInventory,
    }
  }
}

function augmentCartStoreLineItem(
  item: ExternalCartItem,
  variantsBySku: VariantsBySku,
  locale: string
): CartItem {
  const variant = variantsBySku[item.sku]
  const basicFields = getBasicFields(item)
  if (!variant) {
    return basicFields
  }

  const discountPct = parseFloat(
    item?.attributes.find(e => e.key === '_kitDiscountPct')?.value ?? ''
  )
  const validDiscountPct = !isNaN(discountPct) && discountPct > 0 && discountPct <= 100
  const discountMultiplier = validDiscountPct ? (100 - discountPct) / 100 : 1
  const hydratedFields = getHydratedFields(variant, discountMultiplier, locale)

  return {
    ...basicFields,
    variantId: hydratedFields.shopifyVariantId,
    productType: hydratedFields.categoryId,
    ...hydratedFields,
  }
}

export function areCartLinesEqual(
  itemA: Omit<CartLine, 'cost' | 'discountAllocations' | 'id' | 'quantity'>,
  itemB: Omit<CartLine, 'cost' | 'discountAllocations' | 'id' | 'quantity'>
): boolean {
  return isEqual(
    {
      attributes: itemA.attributes?.filter(attr => {
        return attr?.key !== '__typename' && attr?.key !== '_collectionAttribution'
      }),
      sku: itemA.sku,
    },
    {
      attributes: itemB.attributes?.filter(attr => {
        return attr?.key !== '__typename' && attr?.key !== '_collectionAttribution'
      }),
      sku: itemB.sku,
    }
  )
}

type MakeCartItemOptions = {
  properties?: Record<string, string | undefined>
  quantity?: number
  attributedPortal:
    | undefined
    | {
        name: string
        itemType: 'PortalLogo' | 'PortalColor' | 'Other'
      }
}

export type MakeCartItemProps = { options: MakeCartItemOptions } & {
  product: {
    handle: string
  }
  variantGroup: {
    siteWidePromoEligible: boolean
    finalSale: boolean
    primaryCategory: Pick<ProductCatalogVariantGroup['primaryCategory'], 'analyticsId'>
  }
  variant: {
    shopifyId: string
    shopifyProductId: string
    sku: string
    price: {
      amount: number
      currencyCode: string
    }
    compareAtPrice?: null | {
      amount: number
    }
  }
}

export type EnhanceCartItemProps = { options: MakeCartItemOptions } & {
  item: ZidiGtmVariant // Replace with GtmVariant once ZidiGtmVariant type is deleted.
  finalSale: boolean
}

export function useMakeCartItem() {
  const { locale } = useLocalization()
  const params = useParams()
  const pathname = usePathname()
  const translations = useTranslation()

  const collectionHandle: string | undefined = useMemo(() => {
    if (pathname?.startsWith('/collections') && !(params?.handle instanceof Array)) {
      return params?.handle ?? undefined
    }
    return undefined
  }, [params, pathname])

  return useCallback(
    (props: MakeCartItemProps | EnhanceCartItemProps): UseCartNewCartItem[] => {
      const isFinalSale = 'item' in props ? props.finalSale : props.variantGroup.finalSale

      const properties = {
        ...(isFinalSale
          ? { [translations.cart.finalSale]: translations.cart.nonReturnable, _LPROP: 'final sale' }
          : {}),
        ...(collectionHandle ? { _collectionAttribution: collectionHandle } : {}),
        ...(props.options.attributedPortal?.name
          ? { _portalAttribution: props.options.attributedPortal.name }
          : {}),
        ...(props.options.attributedPortal?.itemType === 'PortalColor'
          ? { _isPortalColor: 'true' }
          : {}),
        ...(props.options.attributedPortal?.itemType === 'PortalLogo'
          ? { _isPortalLogo: 'true' }
          : {}),
        ...(props.options.properties ?? {}),
      }

      return [
        {
          finalSale: isFinalSale,
          quantity: props.options.quantity || 1,
          properties,
          item: {
            shopifyId: 'item' in props ? props.item.shopifyId : props.variant.shopifyId,
            externalParentId:
              'item' in props ? props.item.externalParentId : props.variant.shopifyProductId,
            currencyType:
              'item' in props ? props.item.currencyType : props.variant.price.currencyCode,
            handle: 'item' in props ? props.item.handle : props.product.handle,
            sku: 'item' in props ? props.item.sku : props.variant.sku,
            category:
              'item' in props
                ? props.item.category
                : props.variantGroup.primaryCategory.analyticsId,
            price:
              'item' in props
                ? props.item.price
                : getDollarsAndCentsForAmount({
                    value: props.variant.compareAtPrice?.amount ?? props.variant.price.amount,
                    unit: 'dollars',
                    currency: props.variant.price.currencyCode,
                    locale: locale,
                  }).cents,
            discountPrice:
              'item' in props
                ? props.item.discountPrice
                : props.variant.compareAtPrice?.amount
                ? getDollarsAndCentsForAmount({
                    value: props.variant.price.amount,
                    unit: 'dollars',
                    currency: props.variant.price.currencyCode,
                    locale: locale,
                  }).cents
                : null,
          },
        },
      ]
    },
    [collectionHandle, locale, translations.cart.finalSale, translations.cart.nonReturnable]
  )
}
